import gql from 'graphql-tag';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocalStorage } from '@zapier/common-hooks';
import { useMediaProps } from '@zapier/responsive-utils';
import { UNIVERSAL_TOPBAR_CONDENSED_HEIGHT, UNIVERSAL_TOPBAR_HEIGHT, UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH, UNIVERSAL_SIDEBAR_WIDTH, } from '../constants';
const defaultContext = {
    canForceSidebarOpen: false,
    canTopbarFitBackToAppButton: false,
    canTopbarFitLinks: false,
    currentAccountId: undefined,
    currentUrl: '',
    isLoadingLoggedIn: true,
    isLoggedIn: false,
    isOnAppPage: false,
    isSearchFullWidth: true,
    isSidebarControlledByTopbar: false,
    isSidebarForcedOpen: false,
    isSidebarOpenedByTopbar: false,
    isSidebarRendered: false,
    onToggleSidebarVisibility: () => { },
    setIsSidebarForcedOpen: () => { },
    setIsSidebarOpenedByTopbar: () => { },
    setIsSidebarRendered: () => { },
    shouldRenderSearch: true,
    shouldRenderSidebar: false,
    shouldRenderTopbarLinks: false,
    shouldRenderUniversalNav: true,
    sidebarWidth: 0,
    topbarSize: 'medium',
    topbarHeight: UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
    useQuery: () => { },
};
const UniversalNavContext = createContext(defaultContext);
export const useUniversalNavContext = () => useContext(UniversalNavContext);
export const isAuthenticatedQuery = gql `
  query isAuthenticatedQuery {
    isAuthenticatedV2
  }
`;
const isCurrentUrlAnAuthPage = (currentUrl) => {
    return (currentUrl.includes('/login') ||
        currentUrl.includes('/sign-up') ||
        currentUrl.includes('/oauth/authorize'));
};
const isCurrentUrlAnAppPage = (currentUrl) => {
    return currentUrl.includes('/app/');
};
const isCurrentUrlAnOnboardingPage = (currentUrl) => {
    return currentUrl.includes('/app/onboarding');
};
const shouldRenderSidebarForRoute = ({ currentUrl, }) => {
    return (isCurrentUrlAnAppPage(currentUrl) &&
        !isCurrentUrlAnAuthPage(currentUrl) &&
        !isCurrentUrlAnOnboardingPage(currentUrl));
};
const shouldRenderTopbarLinksForRoute = ({ currentUrl, }) => {
    return (!isCurrentUrlAnAppPage(currentUrl) && !isCurrentUrlAnAuthPage(currentUrl));
};
const getShouldRenderSidebar = ({ isLoggedIn, canTopbarFitLinks, currentUrl, isSidebarControlledByTopbar, isSidebarOpenedByTopbar, }) => {
    if (typeof window === 'undefined') {
        return false;
    }
    if (isSidebarControlledByTopbar) {
        return isSidebarOpenedByTopbar;
    }
    if (isLoggedIn && shouldRenderSidebarForRoute({ currentUrl })) {
        return true;
    }
    if (!canTopbarFitLinks && shouldRenderTopbarLinksForRoute({ currentUrl })) {
        return true;
    }
    return false;
};
const getShouldRenderSearch = ({ currentUrl }) => {
    if (isCurrentUrlAnAuthPage(currentUrl) ||
        isCurrentUrlAnOnboardingPage(currentUrl)) {
        return false;
    }
    return true;
};
const getShouldRenderTopbarToggle = ({ isSidebarControlledByTopbar, isLoggedIn, currentUrl, }) => {
    if (!isSidebarControlledByTopbar) {
        return false;
    }
    if (isLoggedIn) {
        return true;
    }
    return shouldRenderTopbarLinksForRoute({
        currentUrl,
    });
};
const getSidebarWidth = ({ canForceSidebarOpen, isSidebarForcedOpen, isSidebarRendered, isSidebarControlledByTopbar, }) => {
    if (!isSidebarRendered || isSidebarControlledByTopbar) {
        return 0;
    }
    if (canForceSidebarOpen && isSidebarForcedOpen) {
        return UNIVERSAL_SIDEBAR_WIDTH;
    }
    return UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH;
};
export const UniversalNavProvider = (props) => {
    const { currentUrl, useQuery } = props;
    const isOnAppPage = isCurrentUrlAnAppPage(currentUrl);
    const { isSearchFullWidth, isSidebarControlledByTopbar, canForceSidebarOpen, canTopbarFitBackToAppButton, canTopbarFitLinks, topbarSize, topbarHeight, } = useMediaProps({
        '(min-width: 0px)': {
            isSidebarControlledByTopbar: true,
            isSearchFullWidth: true,
            canForceSidebarOpen: false,
            canTopbarFitBackToAppButton: false,
            canTopbarFitLinks: false,
            topbarSize: 'small',
            topbarHeight: UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
        },
        '(min-width: 450px)': {
            canTopbarFitBackToAppButton: true,
            topbarSize: 'medium',
            topbarHeight: UNIVERSAL_TOPBAR_HEIGHT,
        },
        '(min-width: 960px)': {
            isSidebarControlledByTopbar: false,
            isSearchFullWidth: false,
            canTopbarFitLinks: true,
        },
        '(min-width: 1280px)': {
            canForceSidebarOpen: shouldRenderSidebarForRoute({ currentUrl }),
        },
    });
    const shouldRenderTopbarLinks = canTopbarFitLinks &&
        shouldRenderTopbarLinksForRoute({
            currentUrl,
        });
    const authResult = useQuery(isAuthenticatedQuery, {
        fetchPolicy: 'cache-and-network',
        ssr: false,
    });
    const isLoggedIn = authResult.data && authResult.data.isAuthenticatedV2;
    const [isSidebarOpenedByTopbar, setIsSidebarOpenedByTopbar] = useState(false);
    const [isSidebarForcedOpen, setIsSidebarForcedOpen] = useLocalStorage('UniversalNav:isSidebarForcedOpen');
    useEffect(() => {
        if (isSidebarForcedOpen === null || isSidebarForcedOpen === undefined) {
            setIsSidebarForcedOpen(true);
        }
    }, [isSidebarForcedOpen, setIsSidebarForcedOpen]);
    const [isSidebarRendered, setIsSidebarRendered] = useState(false);
    const shouldRenderSidebar = getShouldRenderSidebar({
        isLoggedIn,
        canTopbarFitLinks,
        currentUrl,
        isSidebarControlledByTopbar,
        isSidebarOpenedByTopbar,
    });
    const shouldRenderSearch = getShouldRenderSearch({
        currentUrl,
    });
    const shouldRenderTopbarToggle = getShouldRenderTopbarToggle({
        isSidebarControlledByTopbar,
        isLoggedIn,
        currentUrl,
    });
    const onToggleSidebarVisibility = shouldRenderTopbarToggle
        ? () => {
            setIsSidebarOpenedByTopbar(!isSidebarOpenedByTopbar);
        }
        : undefined;
    const sidebarWidth = getSidebarWidth({
        canForceSidebarOpen,
        isSidebarForcedOpen,
        isSidebarRendered,
        isSidebarControlledByTopbar,
    });
    const value = {
        currentAccountId: props.currentAccountId,
        isLoadingLoggedIn: authResult.loading,
        canTopbarFitBackToAppButton,
        canTopbarFitLinks,
        canForceSidebarOpen,
        currentUrl,
        isLoggedIn,
        isOnAppPage,
        isSearchFullWidth,
        isSidebarControlledByTopbar,
        isSidebarOpenedByTopbar,
        isSidebarForcedOpen: canForceSidebarOpen && isSidebarRendered ? isSidebarForcedOpen : false,
        isSidebarRendered,
        onToggleSidebarVisibility,
        setIsSidebarOpenedByTopbar,
        setIsSidebarForcedOpen,
        setIsSidebarRendered,
        shouldRenderSearch,
        shouldRenderSidebar,
        shouldRenderTopbarLinks,
        shouldRenderUniversalNav: true,
        sidebarWidth,
        topbarHeight,
        topbarSize,
        useQuery,
    };
    return (React.createElement(UniversalNavContext.Provider, { value: value }, props.children));
};
//# sourceMappingURL=useUniversalNavContext.js.map
// @zapier/universal-layout@5.12.61 (4902ab0c1)
